import React, { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useAuth0 } from "@auth0/auth0-react";
import API_BASE_URL from "../config";

type TripFormProps = {
  onTripAdded: () => void;
  getToken: () => Promise<string>;
};

const TripForm: React.FC<TripFormProps> = ({ onTripAdded, getToken }) => {
  const [name, setName] = useState("");
  const [tripDate, setTripDate] = useState(""); // ✅ New state for trip date
  const [tripEndDate, setTripEndDate] = useState("");
  const { user } = useAuth0();
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const token = await getToken();
    setSubmitting(true);

    const newTrip = {
      name,
      tripDate,
      tripEndDate, // ✅ Include in payload
      createdByUserId: user?.sub || "",
      createdByUserName: user?.name || "Unknown",
      createdByEmail: user?.email || "Unknown",
      createdAt: new Date().toISOString(),
    };

    const response = await fetch(`${API_BASE_URL}/trips`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(newTrip),
    });

    if (response.ok) {
      setName("");
      setTripDate(""); // ✅ Reset form
      setTripEndDate("");
      onTripAdded();
      setShow(false);
    } else {
      console.error("Error creating trip");
    }
    setSubmitting(false);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="h5 mb-0">Quick actions</h3>
          <small className="text-muted">Create trips and invite participants. Collaborators invited will see details once added.</small>
        </div>
        <div>
          <Button variant="primary" onClick={() => setShow(true)}>
            Start Trip
          </Button>
        </div>
      </div>

      <Modal show={show} onHide={() => setShow(false)} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Start Trip</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted">Create a trip. Fill the basic information below. Dates are optional but helpful for scheduling.</p>

            <Form.Group controlId="tripName">
                <Form.Label>Trip name</Form.Label>
              <Form.Control
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Trip name"
                required
              />
            </Form.Group>

            <div className="row">
              <div className="col-12 col-md-6 mt-3">
                <Form.Group controlId="tripDate">
                  <Form.Label>Start date</Form.Label>
                  <Form.Control
                    type="date"
                    value={tripDate}
                    onChange={(e) => setTripDate(e.target.value)}
                  />
                </Form.Group>
              </div>
              <div className="col-12 col-md-6 mt-3">
                <Form.Group controlId="tripEndDate">
                  <Form.Label>End date</Form.Label>
                  <Form.Control
                    type="date"
                    value={tripEndDate}
                    onChange={(e) => setTripEndDate(e.target.value)}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="mt-3">
              <small className="text-muted">You will be added as the creator. Invite participants later from the trip details page.</small>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShow(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={submitting || !name.trim()}>
              {submitting ? "Creating…" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default TripForm;
